import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { EnelXGui } from 'gui-com-lib';
import {
  validateRegister,
} from '../../utils/formValidation';
import { CustomModal } from '../CustomModal';
import { PasswordValidation } from '../common/PasswordValidation';
import {
  StyledForm, StyledButton, Header, Divider,
} from '../../main.styled';
import { toastError } from '../common/ToastError';
import { generateRandomId, normalizedLanguage } from '../../utils/common';
import { getFromStorage, setInStorage } from '../../utils/storage';
import { accountValidation, accountVerification, registerAccount } from '../../api';

const paths = window.location.pathname.split('/');

const {
  SingleInput, Button,
} = EnelXGui.EnelXGuiAtoms;

const { Loader } = EnelXGui.EnelXGuiComponents;

const Register = () => {
  const { t } = useTranslation();
  const [validationError, setValidationError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalResult, setModalResult] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [session, setSession] = useState(getFromStorage('userId'));
  const params = new URLSearchParams(document.location.search);
  const [newPassword, setNewPassword] = useState('');
  const [verifiedPassword, setVerifiedPassword] = useState('');
  const [accountOk, setAccountOk] = useState(false);
  const [language, setLanguage] = useState(paths[1] || null);
  const [country, setCountry] = useState(paths[2] || null);

  const validationCode = params.get('confirmation');
  const loginUrl = params.get('loginUrl');
  const source = params.get('source');

  const headers = {};

  // TODO: implement another api if needed since geolocation-db.com is down
  // const getIpData = async () => {
  //   try {
  //     const res = await axios.get('https://geolocation-db.com/json/');
  //     setIp(res.data.IPv4 || null);
  //     localStorage.setItem('userIp', JSON.stringify(res.data.IPv4));
  //   } catch (error) {
  //     setIp(null);
  //   }
  // };

  const acc = {
    country: country?.toUpperCase(),
    language: language ? normalizedLanguage(language) : null,
    sessionId: 'string',
    touchpoint: 'string',
    validationCode,
    source,
  };

  useEffect(() => {
    if (!session) {
      const sId = generateRandomId();
      setSession(sId);
      setInStorage('userId', sId);
    }
  }, []);

  const accountValidated = async () => {
    try {
      setIsFetching(true);
      const response = await accountValidation(acc, headers);
      if (response?.data?.data?.success || response?.data?.data?.enelId) {
        setModalOpen(true);
        setIsFetching(false);
      }
      return response?.data?.message;
    } catch (error) {
      setValidationError(true);
      setIsFetching(false);
    }
    return null;
  };

  useEffect(() => {
    accountValidated();
  }, []);

  const validLoginUrl = loginUrl && loginUrl !== '';

  const onClose = () => {
    if (validLoginUrl) {
      window.location.href = loginUrl;
    }
  };

  const onSubmit = async () => {
    if (validateRegister(newPassword) && (newPassword === verifiedPassword)) {
      const accountToRegister = {
        country: country?.toUpperCase(),
        language: normalizedLanguage(language),
        newPassword,
        sessionId: 'string',
        touchpoint: 'string',
        source,
      };

      try {
        setIsFetching(true);

        const verifiedAccount = await accountVerification(acc, headers);

        const registeredAccount = await registerAccount(accountToRegister, headers, verifiedAccount?.data?.data?.enelId);
        if (verifiedAccount.data.data.enelId && registeredAccount.data.data.success) {
          setIsFetching(false);
          setAccountOk(true);
          setModalOpen(false);
          setModalResult(true);
        }
      } catch (error) {
        setIsFetching(false);
        toastError(t('Register_Failed'));
      }

      setIsFetching(false);
      if (!validateRegister(newPassword)) {
        toastError(t('Toast_Pass_Incorrect'));
      }

      if (newPassword !== verifiedPassword) {
        toastError(t('Toast_Pass_Mismatch'));
      }
    }
  };

  return (
    <>
      {
        isFetching && <Loader isSpinning />
      }
      {validationError && (
        <CustomModal
          height="auto"
          width="432px"
          className="modal"
          closeIcon={!!validLoginUrl}
          closeModal={onClose}
          render={() => (
            <>
              <Header medium center>
                {t('Invalid_Validation_Code')}
              </Header>
              {!!validLoginUrl && (
                <StyledButton>
                  <Button
                    btnType="primary"
                    btnText={t('OK')}
                    width="84px"
                    height="48px"
                    onClick={onClose}
                  />
                </StyledButton>
              )}
            </>
          )}
        />
      )}
      { (!validationError && modalOpen) && (
        <CustomModal
          height="901"
          width="432"
          position="relative"
          closeIcon
          closeModal={() => onClose()}
          render={() => (
            <StyledForm>
              <Header>{t('Register_Title')}</Header>
              <SingleInput
                width="100%"
                type="password"
                placeholder={t('Password_Placeholder')}
                id="password"
                onChange={(e) => setNewPassword(e)}
              />
              <SingleInput
                width="100%"
                type="password"
                placeholder={t('Confirm_Password_Placeholder')}
                id="password"
                onChange={(e) => setVerifiedPassword(e)}
              />
              <PasswordValidation password={newPassword} verifiedPassword={verifiedPassword} />
              <Divider />
              <StyledButton>
                <Button
                  btnType="primary"
                  btnText={t('send')}
                  width="124px"
                  height="48px"
                  onClick={onSubmit}
                />
              </StyledButton>
            </StyledForm>
          )}
        />
      )}
      {modalResult && (
        <CustomModal
          height="auto"
          width="432px"
          className="modal"
          closeIcon={!!validLoginUrl}
          closeModal={onClose}
          render={() => (
            <>
              <Header medium center>
                {accountOk ? t('Register_Success') : t('Register_Failed')}
              </Header>
              {!!validLoginUrl && (
                <StyledButton>
                  <Button
                    btnType="primary"
                    btnText={t('OK')}
                    width="84px"
                    height="48px"
                    onClick={onClose}
                  />
                </StyledButton>
              )}
            </>
          )}
        />
      )}
    </>
  );
};

export { Register };
